/**
 * Before any socket: is this a desktop, does the browser have what a session
 * needs, and will it give us the microphone?
 *
 * The microphone prompt has no timeout of its own. After 20 s the page says
 * where the Allow button is; after 60 s it offers to run the check again or to
 * stop here. Stopping here reports the device as incompatible; the page moves
 * on only once the server has the report, and says so if it did not land.
 */

import { useCallback, useEffect, useState } from 'react'
import type { DeviceReport } from './api'
import { MIC_GIVE_UP_AFTER_MS, MIC_HINT_AFTER_MS, isDesktop, missingApis } from './deviceChecks'

interface TechCheckProps {
  onPass(): void
  /** "I can't continue on this device", or a check that cannot pass here. */
  onIncompatible(reason: string): void
  onSlowPermission?(): void
  /** The device report, once "I can't continue" has been pressed. */
  report?: DeviceReport | null
}

type Status = 'checking' | 'asking' | 'failed'

export function TechCheck({ onPass, onIncompatible, onSlowPermission, report }: TechCheckProps) {
  const [status, setStatus] = useState<Status>('checking')
  const [problem, setProblem] = useState<string | null>(null)
  const [waitedMs, setWaitedMs] = useState(0)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setProblem(null)
    setWaitedMs(0)

    const desktop = isDesktop({
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
      innerWidth: window.innerWidth,
    })
    if (!desktop) {
      setStatus('failed')
      setProblem('This study needs a desktop or laptop with a keyboard.')
      return
    }
    const missing = missingApis({
      getUserMedia: typeof navigator.mediaDevices?.getUserMedia === 'function',
      RTCPeerConnection: typeof window.RTCPeerConnection === 'function',
      MediaRecorder: typeof window.MediaRecorder === 'function',
    })
    if (missing.length > 0) {
      setStatus('failed')
      setProblem(`This browser can't run the study (missing ${missing.join(', ')}). Try a recent Chrome, Edge or Firefox.`)
      return
    }

    setStatus('asking')
    const startedAt = Date.now()
    const tick = setInterval(() => setWaitedMs(Date.now() - startedAt), 1000)
    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop())
        if (!cancelled) onPass()
      })
      .catch((err: DOMException) => {
        if (cancelled) return
        setStatus('failed')
        setProblem(
          err?.name === 'NotAllowedError'
            ? 'The microphone was blocked. Allow it in the address bar, then run the check again.'
            : err?.name === 'NotFoundError'
              ? 'No microphone was found on this computer.'
              : `The browser could not open the microphone (${err?.name ?? 'unknown error'}).`,
        )
      })
      .finally(() => clearInterval(tick))
    return () => {
      cancelled = true
      clearInterval(tick)
    }
  }, [attempt, onPass])

  const slow = status === 'asking' && waitedMs >= MIC_HINT_AFTER_MS
  const stuck = status === 'asking' && waitedMs >= MIC_GIVE_UP_AFTER_MS
  useEffect(() => {
    if (slow) onSlowPermission?.()
  }, [slow, onSlowPermission])

  const giveUp = useCallback(() => onIncompatible(problem ?? 'The microphone check could not be completed.'), [onIncompatible, problem])

  return (
    <section className="gate">
      <p className="gate-step">Before we start</p>
      <h1>Checking your computer</h1>
      <p className="hint">You need a desktop or laptop, a microphone, and speakers or headphones.</p>
      {status === 'checking' && <p className="hint">Checking…</p>}
      {status === 'asking' && <p className="hint">Click Allow when your browser asks for the microphone.</p>}
      {slow && (
        <p className="notice" role="status">
          Still waiting. Look for the prompt near the address bar and click Allow.
        </p>
      )}
      {problem && <p className="error">{problem}</p>}
      {report?.status === 'failed' && (
        <p className="error" role="alert" data-testid="tech-report-failed">
          {report.message}
        </p>
      )}
      <div className="gate-actions">
        {(status === 'failed' || stuck) && (
          <button type="button" onClick={() => setAttempt((n) => n + 1)} data-testid="tech-retry">
            Check again
          </button>
        )}
        {(status === 'failed' || stuck) && (
          <button type="button" onClick={giveUp} disabled={report?.status === 'sending'} data-testid="tech-give-up">
            I can't continue on this device
          </button>
        )}
      </div>
    </section>
  )
}
